import type {
  AnalysisVoicing,
  AnalysisHandVoicingEntry,
} from "@/lib/music/analyzer-types";

export const ROOT_PITCH_CLASS: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3,
  E: 4, F: 5, "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8,
  A: 9, "A#": 10, Bb: 10, B: 11,
};

export const QUALITY_INTERVALS: Record<string, number[]> = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  diminished: [0, 3, 6],
  augmented: [0, 4, 8],
  "7": [0, 4, 7, 10],
  dominant7: [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  min7: [0, 3, 7, 10],
  m7b5: [0, 3, 6, 10],
  "half-dim7": [0, 3, 6, 10],
  dim7: [0, 3, 6, 9],
  aug7: [0, 4, 8, 10],
  minmaj7: [0, 3, 7, 11],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  "6": [0, 4, 7, 9],
  maj6: [0, 4, 7, 9],
  m6: [0, 3, 7, 9],
  min6: [0, 3, 7, 9],
  power: [0, 7],
  add9: [0, 2, 4, 7],
  "m(add9)": [0, 2, 3, 7],
  "9": [0, 2, 4, 7, 10],
  maj9: [0, 2, 4, 7, 11],
  m9: [0, 2, 3, 7, 10],
  "6/9": [0, 2, 4, 7, 9],
  "7sus4": [0, 5, 7, 10],
};

const SHARP_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

/** Spells a pitch class as a note name, preferring the same accidental flavour as the root. */
export function pitchClassToName(pc: number, rootSpelling: string): string {
  const useFlats = rootSpelling.includes("b");
  const arr = useFlats ? FLAT_NAMES : SHARP_NAMES;

  return arr[((pc % 12) + 12) % 12];
}

const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const LETTER_PITCH_CLASS = [0, 2, 4, 5, 7, 9, 11];

/**
 * De combien de lettres on monte pour atteindre un intervalle, en demi-tons.
 *
 * Une tierce s'écrit toujours sur la deuxième lettre au-dessus de la
 * fondamentale, mineure ou majeure ; une quinte sur la quatrième, juste,
 * diminuée ou augmentée. C'est cette table-là qui manque à
 * `pitchClassToName`, qui ne regarde que l'accidentel de la fondamentale et
 * écrit donc *la# mineur* pour la tierce de *sol mineur*.
 *
 * Les trois cases ambiguës sont tranchées pour le répertoire : six demi-tons
 * est la quinte diminuée d'un accord diminué plutôt qu'une quarte augmentée,
 * huit la quinte augmentée plutôt qu'une sixte mineure, neuf la sixte plutôt
 * que la septième diminuée — personne ne veut lire *si double bémol*.
 */
const DEGREE_OF_SEMITONE = [0, 1, 1, 2, 2, 3, 4, 4, 4, 5, 6, 6];

/**
 * Le nom d'une note **de l'accord**, épelé sur le degré qu'elle y occupe.
 *
 * `pitchClassToName` répond à une autre question : « comment écrit-on cette
 * hauteur ». Celle-ci répond à « comment s'appelle cette note *dans cet
 * accord-là* », et les deux divergent dès que la fondamentale n'a pas
 * l'accidentel de ses propres notes : la tierce de sol mineur est un si bémol,
 * jamais un la dièse, et aucune règle sur l'orthographe de « sol » ne peut le
 * dire.
 *
 * L'algorithme est celui qu'on apprend : la lettre monte du nombre de degrés de
 * l'intervalle, l'accidentel est ce qu'il faut pour tomber juste. Il rend
 * `null` au-delà du double accidentel, où il n'y a plus de nom raisonnable et
 * où l'appelant fait mieux de retomber sur l'orthographe simple.
 */
export function chordToneName(
  rootSpelling: string,
  rootPc: number,
  pc: number,
): string | null {
  const letter = letterIndex(rootSpelling);

  if (letter < 0) {
    return null;
  }

  const semitones = (((pc - rootPc) % 12) + 12) % 12;

  return spellOnLetter(letter + DEGREE_OF_SEMITONE[semitones]!, pc);
}

/**
 * L'index de la lettre d'un nom de note, `C` à 0 et `Si♭` à 6, ou `-1`.
 *
 * Les deux orthographes de l'accidentel sont acceptées — l'analyseur écrit
 * `Bb`, les surfaces d'affichage écrivent `Si♭` — parce que seule la lettre
 * compte ici et qu'elle est au même endroit dans les deux.
 */
export function letterIndex(name: string): number {
  return LETTERS.indexOf(name.trim().slice(0, 1).toUpperCase());
}

/**
 * La note qui s'écrit **sur cette lettre-là** et sonne cette hauteur.
 *
 * La moitié basse de `chordToneName`, séparée parce que la gamme la demande
 * aussi et qu'elle n'a pas de fondamentale : son sixième degré est une sixte
 * mineure quand l'accord y voit une quinte augmentée, donc elle nomme sa lettre
 * elle-même au lieu de la déduire d'un intervalle. Recopier la table des
 * lettres de ce côté-là en aurait fait deux, qui divergeraient.
 *
 * L'index tourne — `spellOnLetter(8, ...)` est la deuxième lettre — et le
 * résultat est `null` au-delà du double accidentel, où il n'y a plus de nom
 * raisonnable.
 */
export function spellOnLetter(letter: number, pc: number): string | null {
  const target = ((letter % 7) + 7) % 7;
  const natural = LETTER_PITCH_CLASS[target]!;

  let shift = (((pc - natural) % 12) + 12) % 12;

  if (shift > 6) {
    shift -= 12;
  }

  if (Math.abs(shift) > 2) {
    return null;
  }

  return LETTERS[target]! + (shift > 0 ? "#".repeat(shift) : "b".repeat(-shift));
}

/**
 * De combien de lettres on monte depuis la **tonique** pour atteindre un
 * demi-ton de l'octave.
 *
 * Ce n'est pas `DEGREE_OF_SEMITONE`, et les deux ne peuvent pas fusionner :
 * six demi-tons sont la quinte diminuée d'un accord et la quarte augmentée
 * d'une gamme, huit sa quinte augmentée et sa sixte mineure. Un accord empile
 * des tierces, une gamme parcourt ses sept lettres ; ce sont deux questions,
 * et cette table-ci répond à la seconde.
 *
 * Elle ne dépend pas du mode. Les sept lettres d'une tonalité sont les mêmes
 * en majeur et en mineur — seules changent leurs altérations, et celles-là
 * `spellOnLetter` les calcule sur la hauteur réelle. Une même table écrit donc
 * le `sol♯` sensible de la mineur, le `fa♯` sensible de sol majeur et le
 * `si♭` du septième degré de do.
 *
 * Les cinq cases chromatiques sont tranchées pour le répertoire tonal, dans
 * l'ordre où elles s'y rencontrent : le second degré est **abaissé**
 * (napolitain), le quatrième est **haussé** (dominante secondaire), les
 * troisième, sixième et septième sont abaissés (emprunts au mineur). En
 * mineur, la même table donne la sensible et le sixième degré haussés, qui
 * sont les altérations des gammes harmonique et mélodique.
 */
const SCALE_DEGREE_OF_SEMITONE = [0, 1, 1, 2, 2, 3, 3, 4, 5, 5, 6, 6];

/**
 * Le nom d'une note **dans une tonalité**, épelé sur le degré qu'elle y occupe.
 *
 * La troisième question, à côté des deux que se posent déjà `pitchClassToName`
 * (« comment écrit-on cette hauteur ») et `chordToneName` (« comment s'appelle
 * cette note dans cet accord-là ») : comment s'appelle-t-elle **dans ce
 * morceau**. C'est celle qu'il faut poser pour toute note que l'accord ne
 * nomme pas, parce que la réponse des deux autres bouge alors avec l'accord et
 * qu'une note qui change de lettre change de ligne sur la portée.
 *
 * Rend `null` au-delà du double accidentel, comme `spellOnLetter` dont elle
 * n'est qu'un décalage, et sur une tonique qu'on ne sait pas lire.
 */
export function keyToneName(pc: number, tonicSpelling: string): string | null {
  const letter = letterIndex(tonicSpelling);

  if (letter < 0) {
    return null;
  }

  const tonic = ROOT_PITCH_CLASS[tonicSpelling.trim().replace(/♭/g, "b").replace(/♯/g, "#")];

  if (tonic === undefined) {
    return null;
  }

  const semitones = (((pc - tonic) % 12) + 12) % 12;

  return spellOnLetter(letter + SCALE_DEGREE_OF_SEMITONE[semitones]!, pc);
}

/**
 * Les classes de hauteur qu'un accord **énonce**, ou `null` s'il est illisible.
 *
 * Ce que l'accord empile, et non ce que la main joue au même moment : ce sont
 * deux choses différentes, et confondre les deux était exactement le défaut de
 * la première version. Sur une gamme de vingt notes par-dessus un `Eb/Bb`, la
 * pile de la main droite couvrait huit classes de hauteur sur douze, si bien
 * que presque toute la mesure se faisait orthographier par un accord de mi♭ —
 * et le `la` en sortait `si♭♭`, une quinte diminuée qui n'a jamais été là.
 * L'accord, lui, en énonce trois : mi♭, sol, si♭.
 *
 * C'est le même verdict que le `tone` que l'analyseur pose sur chaque note
 * (`root`, `3rd`, `5th`, `non-chord`) : vérifié note à note sur *Libérée,
 * délivrée*, les deux coïncident sur 1815 notes sur 1815. La qualité est
 * préférée parce qu'elle tient dans l'accord, qui est déjà là, quand le `tone`
 * demanderait de faire descendre l'annotation de chaque note à travers tout le
 * parcours jusqu'au dessin.
 */
export function chordPitchClasses(
  rootPitchClass: number,
  quality: string,
): ReadonlySet<number> | null {
  const intervals = QUALITY_INTERVALS[quality];

  if (intervals === undefined) {
    return null;
  }

  const root = (((rootPitchClass % 12) + 12) % 12);

  return new Set(intervals.map((interval) => (root + interval) % 12));
}

/**
 * L'orthographe d'une touche dans une fenêtre d'accord : la règle entière.
 *
 * ## Pourquoi elle est ici et pas deux fois ailleurs
 *
 * `staff.ts` la voulait pour poser la note sur sa ligne, `hand-names.ts` pour
 * écrire son nom sur la plaque, et les deux la recopiaient. Deux copies d'une
 * règle d'orthographe finissent par diverger, et le jour où elles divergent
 * l'écran écrit `si♭` sur la ligne du `la`.
 *
 * ## Ce que l'analyseur a écrit passe avant tout
 *
 * D'une partition il tient la lettre du graveur, et elle est décisive : sur un
 * `Eø7` de Chopin, `la♯` et `si♭` sont la même touche et deux notes
 * différentes, et seule la résolution tranche — aucune règle locale ne la
 * retrouve. D'un fichier MIDI il applique exactement les deux règles ci-dessous,
 * si bien que les deux réponses coïncident : vérifié note à note sur *Libérée,
 * délivrée*, 1815 sur 1815. Sur le prélude op.28 n°4, elles divergent sur
 * vingt-deux notes, et l'analyseur a raison sur les vingt-deux.
 *
 * Ce qui suit n'est donc plus la règle : c'est ce qui reste quand l'analyseur
 * n'a rien dit — une analyse antérieure au champ, ou une touche que la fenêtre
 * ne porte pas parce qu'elle est tenue depuis avant.
 *
 * ## L'accord nomme ce qu'il énonce, la tonalité nomme le reste
 *
 * Une note **de l'accord** s'épelle sur le degré qu'elle y occupe, et pas
 * autrement : la tierce de sol mineur est un `si♭`, jamais un `la♯`, parce
 * qu'une triade s'empile en tierces et qu'aucune tonalité n'a le droit de la
 * défaire. C'est aussi ce qui rend un fa♯ de dominante secondaire correct en
 * do majeur, où l'armure ne dit pourtant aucun dièse.
 *
 * Une note que l'accord **n'énonce pas** n'a pas ce garde-fou. L'épeler quand
 * même sur la fondamentale, c'est la laisser suivre l'accord : la même touche
 * sortait `sol♭` sous un do et `fa♯` sous un ré, donc changeait de lettre,
 * donc changeait de ligne d'une mesure à l'autre sans que rien de musical
 * n'ait bougé. La tonalité, elle, ne bouge pas de tout le niveau, et c'est
 * exactement la stabilité qui manquait.
 *
 * ## L'accord n'a pas le droit d'imposer une double altération
 *
 * `DEGREE_OF_SEMITONE` tranche six demi-tons en quinte diminuée, ce qui est
 * juste sur un accord diminué et faux partout ailleurs : sous un `Eb`, un `la`
 * devient alors la quinte diminuée `si♭♭`. Le cas n'est pas rare — un couple
 * accord × hauteur sur dix y tombe — et il ne se voyait pas tant que rien
 * n'était gravé, parce qu'un `si♭♭` et un `la` se posent sur deux lignes
 * voisines et qu'un nom écrit à côté rattrapait la lecture. Un double bémol
 * gravé, lui, se voit tout de suite, et il est faux.
 *
 * Une double altération reste légitime — la septième d'un `°7`, une sensible
 * dans une tonalité à bémols — mais jamais pour une note que la tonalité écrit
 * simplement. C'est donc la seule chose que l'accord ne peut pas emporter.
 *
 * ## Ce qu'elle ne fait pas
 *
 * Elle ne connaît pas la direction mélodique, qui écrirait `do♯` en montant
 * vers `ré` et `ré♭` en descendant vers `do`. Cette règle-là est vraie, mais
 * elle est l'inverse d'une règle de stabilité : elle donne deux lettres à une
 * même touche dans une même tonalité. Elle ne se poserait qu'après, et sur la
 * note de passage reconnue comme telle, pas sur toute note hors de l'accord.
 *
 * Sans tonalité — un morceau dont la détection n'a rien donné — elle retombe
 * mot pour mot sur ce qui se faisait avant elle.
 */
export function windowToneName({
  spelled = null,
  pitchClass,
  rootSpelling,
  rootPitchClass,
  chordPitchClasses,
  keyTonic,
}: {
  /**
   * L'orthographe que l'analyseur a donnée à cette note, sans son octave.
   *
   * Prise telle quelle quand elle existe. Voir plus haut.
   */
  readonly spelled?: string | null;
  readonly pitchClass: number;
  /** L'orthographe de la fondamentale, qui décide des bémols et des dièses. */
  readonly rootSpelling: string;
  readonly rootPitchClass: number;
  /**
   * Les classes de hauteur que l'accord énonce, ou `null` quand on ne les
   * connaît pas — auquel cas toutes sont réputées siennes, ce qui est le
   * comportement d'avant cette règle.
   */
  readonly chordPitchClasses: ReadonlySet<number> | null;
  /** L'orthographe de la tonique du morceau, ou `null` si aucune n'est connue. */
  readonly keyTonic: string | null;
}): string {
  if (spelled !== null && spelled !== "") {
    return spelled.replace(/♭/g, "b").replace(/♯/g, "#");
  }

  const spelling = rootSpelling.replace(/♭/g, "b").replace(/♯/g, "#");
  const pc = (((pitchClass % 12) + 12) % 12);
  const root = (((rootPitchClass % 12) + 12) % 12);
  const chordTone = chordPitchClasses === null || chordPitchClasses.has(pc);
  const byChord = chordToneName(spelling, root, pc);
  const byKey = keyTonic === null ? null : keyToneName(pc, keyTonic);

  return (
    (chordTone && !(doubled(byChord) && byKey !== null && !doubled(byKey)) ? byChord : null) ??
    byKey ??
    byChord ??
    pitchClassToName(pc, spelling)
  );
}

/** Ce nom porte-t-il un double dièse ou un double bémol ? */
function doubled(name: string | null): boolean {
  return name !== null && (name.includes("##") || name.includes("bb"));
}

/** Splits an id like "Bbm7" / "F#dim" into [root, suffix]. Accepts both
 *  flat and sharp spellings. Returns null for unrecognised input. */
export function splitChordId(
  id: string,
): { root: string; suffix: string } | null {
  if (id.length === 0) {
    return null;
  }

  const twoCharRoot = id.slice(0, 2);

  if (ROOT_PITCH_CLASS[twoCharRoot] !== undefined) {
    return { root: twoCharRoot, suffix: id.slice(2) };
  }

  const oneCharRoot = id.slice(0, 1);

  if (ROOT_PITCH_CLASS[oneCharRoot] !== undefined) {
    return { root: oneCharRoot, suffix: id.slice(1) };
  }

  return null;
}

const UNICODE_SHARP = "♯"; // ♯
const UNICODE_FLAT = "♭"; // ♭

/**
 * Replace ASCII accidentals (`#` and accidental `b`) in a label with the
 * proper Unicode glyphs (♯ / ♭). Use only at display sites, keep ASCII for
 * IDs, comparisons and routing.
 */
export function formatAccidentals(text: string): string {
  return text
    .replace(/#/g, UNICODE_SHARP)
    .replace(/([A-G])b/g, `$1${UNICODE_FLAT}`);
}

const INTERVAL_TO_TONE: Record<number, string> = {
  0: "root",
  2: "9th",
  3: "b3rd",
  4: "3rd",
  5: "11th",
  6: "b5",
  7: "5th",
  8: "b13",
  9: "13th",
  10: "b7th",
  11: "7th",
};

export type ChordToneEntry = {
  /** Pitch class (0–11). */
  pc: number;
  /** Note name, spelled to match the root accidental. */
  name: string;
  /** Tone role within the chord: "root" / "3rd" / "5th" / "7th" / "9th" / etc. */
  tone: string;
};

/** Returns the theoretical chord tones for a root + quality, in interval order. */
export function chordToneStructure(root: string, quality: string): ChordToneEntry[] {
  const rootPc = ROOT_PITCH_CLASS[root];

  if (rootPc === undefined) {
    return [];
  }

  const intervals = QUALITY_INTERVALS[quality];

  if (!intervals) {
    return [];
  }

  return intervals.map((iv) => {
    const pc = (rootPc + iv) % 12;

    return {
      pc,
      name: pitchClassToName(pc, root),
      tone: INTERVAL_TO_TONE[iv] ?? "?",
    };
  });
}

const TONE_LABELS_FR: Record<string, string> = {
  root: "fond",
  "3rd": "3ce",
  b3rd: "♭3",
  "5th": "5te",
  b5: "♭5",
  "7th": "7M",
  b7th: "7m",
  "9th": "9",
  b9: "♭9",
  "11th": "11",
  "#11": "♯11",
  "13th": "13",
  b13: "♭13",
  "6th": "6",
  "#9": "♯9",
  "non-chord": "hors",
  unknown: "?",
};

/** Short French tone label for display in chips/tiles ("fond", "3ce", "13", ...). */
export function toneLabelFR(tone: string): string {
  return TONE_LABELS_FR[tone] ?? tone;
}

export type ToneCategory = "structural" | "extension" | "non-chord";

const STRUCTURAL_TONES = new Set(["root", "3rd", "b3rd", "5th", "b5"]);
const EXTENSION_TONES = new Set([
  "6th", "7th", "b7th", "9th", "b9", "11th", "#11", "13th", "b13", "#9",
]);

/** Three-bucket categorisation of a tone for visual coding. */
export function toneCategory(tone: string): ToneCategory {
  if (STRUCTURAL_TONES.has(tone)) {
    return "structural";
  }

  if (EXTENSION_TONES.has(tone)) {
    return "extension";
  }

  return "non-chord";
}

export function theoreticalPitchClasses(root: string, quality: string): number[] {
  const rootPC = ROOT_PITCH_CLASS[root];
  if (rootPC === undefined) return [];
  const intervals = QUALITY_INTERVALS[quality];
  if (!intervals) return [];
  return intervals.map((i) => (rootPC + i) % 12);
}

/**
 * Note MIDI représentative d'une pitch-class, placée dans une octave
 * confortable (autour de C4-C5). Sert aux previews d'accord, où seule la
 * classe de hauteur est connue et où il faut bien choisir une octave.
 */
export function pcToMidiInOctave(pc: number, baseMidi = 60): number {
  const target = baseMidi - (baseMidi % 12) + pc;

  if (target < baseMidi) {
    return target + 12;
  }

  return target;
}

export function placeMissingMidi(
  pc: number,
  playedMidis: number[],
): number {
  if (playedMidis.length === 0) return pc + 60;
  const min = Math.min(...playedMidis);
  const max = Math.max(...playedMidis);
  const center = (min + max) / 2;
  let best = pc + 60;
  let bestDist = Infinity;
  for (let oct = 1; oct <= 8; oct++) {
    const midi = pc + oct * 12;
    const dist = Math.abs(midi - center);
    if (dist < bestDist) {
      bestDist = dist;
      best = midi;
    }
  }
  return best;
}

export const VOICING_TYPE_LABELS: Record<AnalysisVoicing["type"], string> = {
  block: "Bloc",
  broken: "Bris\u00e9",
  arpeggio: "Arp\u00e8ge",
  mixed: "Mixte",
  melodic: "M\u00e9lodique",
  single: "Simple",
};

export const VOICING_DIRECTION_ARROWS: Record<
  NonNullable<AnalysisVoicing["direction"]>,
  string
> = {
  static: "",
  ascending: "\u2191",
  descending: "\u2193",
  "ascending-descending": "\u2191\u2193",
  "descending-ascending": "\u2193\u2191",
};

export const INVERSION_LABELS: Record<
  NonNullable<AnalysisVoicing["inversion"]>,
  string
> = {
  root: "fonda.",
  "1st": "1er renv.",
  "2nd": "2e renv.",
};

type VoicingDescribable = Pick<AnalysisVoicing, "type" | "direction">;

export function describeVoicing(v: VoicingDescribable): string {
  const parts: string[] = [];
  const type = VOICING_TYPE_LABELS[v.type];
  if (type) parts.push(type);
  if (v.direction) {
    const arrow = VOICING_DIRECTION_ARROWS[v.direction];
    if (arrow) parts.push(arrow);
  }
  return parts.join(" ");
}

/** @deprecated use `describeVoicing`, kept as alias for call-site clarity. */
export const describeHandVoicing = (v: AnalysisHandVoicingEntry): string =>
  describeVoicing(v);

const VOICING_DIRECTION_FR: Record<
  NonNullable<AnalysisVoicing["direction"]>,
  string
> = {
  static: "tenu",
  ascending: "ascendant",
  descending: "descendant",
  "ascending-descending": "aller-retour ↑↓",
  "descending-ascending": "aller-retour ↓↑",
};

const VOICING_PATTERN_FR: Record<string, string> = {
  alberti: "Alberti (basse-haut-milieu-haut)",
  "triplet-arpeggio": "arpège en triolets",
  "stacked": "notes empilées",
  "broken-octave": "octaves brisées",
  "block-chord": "accord plaqué",
  "rolling": "roulé",
};

/**
 * Verbose, didactic description of a voicing, texture, direction, pattern,
 * spread and tempo. Keeps it under ~80 chars so it fits on one UI line.
 */
export function describeVoicingDidactic(v: AnalysisVoicing): string {
  const parts: string[] = [];
  const typeFr = VOICING_TYPE_LABELS[v.type];

  if (typeFr) {
    parts.push(typeFr);
  }

  if (v.pattern) {
    const pat = VOICING_PATTERN_FR[v.pattern] ?? v.pattern;
    parts.push(pat);
  }

  if (v.direction && v.direction !== "static") {
    parts.push(VOICING_DIRECTION_FR[v.direction]);
  }

  if (v.spread !== null && v.spread !== undefined && v.spread > 0) {
    parts.push(`étendue ~${v.spread} demi-tons`);
  }

  return parts.join(" · ");
}

/**
 * Explains the relationship between a chord and its bass note (slash chord
 * or inversion). Returns empty string when the bass IS the root.
 */
export function describeBass(
  rootName: string,
  quality: string,
  bassName: string | null,
): string {
  if (!bassName) {
    return "";
  }

  const rootPc = ROOT_PITCH_CLASS[rootName];
  const bassPc = ROOT_PITCH_CLASS[bassName];

  if (rootPc === undefined || bassPc === undefined || rootPc === bassPc) {
    return "";
  }

  const interval = ((bassPc - rootPc) + 12) % 12;
  const tplIntervals = QUALITY_INTERVALS[quality] ?? [];
  const isChordTone = tplIntervals.includes(interval);

  if (isChordTone) {
    if (interval === 3 || interval === 4) {
      return `Tierce à la basse, 1er renversement.`;
    }

    if (interval === 7) {
      return `Quinte à la basse, 2e renversement.`;
    }

    if (interval === 10 || interval === 11) {
      return `Septième à la basse, 3e renversement.`;
    }

    return `${bassName} à la basse, renversement.`;
  }

  // Bass is foreign to the chord, true slash chord.
  return `Basse ${bassName} étrangère à l'accord, crée une tension passagère.`;
}
